import React, { useState } from 'react';
import { getCuratedNews } from '../utils/api';

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  alert('Copied to clipboard!');
};

const toLinkedIn = (items, dash, start, end) => {
  const top = items.filter(i => i.score >= 7).slice(0, 8);
  return `🚀 AWS News Digest for ${dash.industry} (${start} → ${end})\n\n` +
    `Here are the most relevant AWS announcements for ${dash.industry} professionals:\n\n` +
    top.map((item, i) => `${i + 1}. ${item.title}\n💡 ${item.reason}\n🔗 ${item.link}\n`).join('\n') +
    `\n#AWS #CloudComputing #${dash.industry.replace(/\s+/g, '')} #AWSNews`;
};

const toSlack = (items, dash, start, end) => {
  const top = items.filter(i => i.score >= 6).slice(0, 10);
  return `*🗞️ AWS News Digest — ${dash.name}* (${start} to ${end})\n` +
    `_Curated for: ${dash.industry} | ${dash.role}_\n\n` +
    top.map((item, i) =>
      `*${i + 1}. <${item.link}|${item.title}>* [${item.score}/10]\n> ${item.reason}\n`
    ).join('\n');
};

const toMarkdown = (items, dash, start, end) => {
  return `# AWS News Digest — ${dash.name}\n` +
    `**Period:** ${start} to ${end}\n` +
    `**Audience:** ${dash.industry} · ${dash.role}\n\n` +
    items.map((item, i) =>
      `${i + 1}. **${item.title}** (${item.score}/10)\n   ${item.reason}\n   [Read more](${item.link})\n`
    ).join('\n');
};

const RANGE_DAYS = { daily: 1, weekly: 7, monthly: 30 };

export default function NewsDashboard({ dashboard, onDelete }) {
  const schedule = dashboard.schedule || 'custom';
  const isScheduled = schedule !== 'custom';

  const calcRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (RANGE_DAYS[schedule] || 7));
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  };

  const defaultRange = isScheduled ? calcRange() : { start: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) };

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const curate = async () => {
    setLoading(true);
    setError('');
    setItems(null);
    try {
      const res = await getCuratedNews(dashboard.dashboardId, startDate, endDate);
      setItems(res.items || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{dashboard.name}</h3>
        <button onClick={() => onDelete(dashboard.dashboardId)}
          style={{ background: '#d13212', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>
          Delete
        </button>
      </div>
      <p style={{ fontSize: 13, color: '#666', margin: '4px 0 12px' }}>
        {dashboard.industry} · {dashboard.role} · {dashboard.interests}
        {dashboard.emailConfig?.enabled && (
          <span style={{ marginLeft: 8, padding: '2px 8px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 3, fontSize: 12 }}>
            📧 {dashboard.emailConfig.schedule} → {dashboard.emailConfig.email}
          </span>
        )}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        {!isScheduled && (
          <>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span>to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </>
        )}
        {isScheduled && (
          <span style={{ fontSize: 13, color: '#666' }}>📅 {schedule} · {startDate} → {endDate}</span>
        )}
        <button onClick={curate} disabled={loading}
          style={{ padding: '6px 16px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          {loading ? 'Curating with Bedrock...' : isScheduled ? 'Refresh' : 'Curate News'}
        </button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {items && items.length === 0 && <p style={{ color: '#666' }}>No relevant news found for this date range.</p>}

      {items && items.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button onClick={() => copyToClipboard(toLinkedIn(items, dashboard, startDate, endDate))}
              style={{ padding: '6px 14px', background: '#0a66c2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
              📋 Copy for LinkedIn
            </button>
            <button onClick={() => copyToClipboard(toSlack(items, dashboard, startDate, endDate))}
              style={{ padding: '6px 14px', background: '#4a154b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
              📋 Copy for Slack
            </button>
            <button onClick={() => copyToClipboard(toMarkdown(items, dashboard, startDate, endDate))}
              style={{ padding: '6px 14px', background: '#333', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
              📋 Copy Markdown
            </button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f0f0f0', textAlign: 'left' }}>
              <th style={{ padding: 8, width: 40 }}>Score</th>
              <th style={{ padding: 8 }}>Title</th>
              <th style={{ padding: 8, width: 100 }}>Date</th>
              <th style={{ padding: 8 }}>Why it matters</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', width: 28, height: 28, lineHeight: '28px', borderRadius: '50%', textAlign: 'center',
                    background: item.score >= 8 ? '#1b8901' : item.score >= 5 ? '#ec7211' : '#aaa', color: '#fff', fontWeight: 'bold', fontSize: 13,
                  }}>{item.score}</span>
                </td>
                <td style={{ padding: 8 }}>
                  <a href={item.link} target="_blank" rel="noreferrer" style={{ color: '#0073bb' }}>{item.title}</a>
                </td>
                <td style={{ padding: 8, fontSize: 12, color: '#666' }}>{item.publishedAt?.slice(0, 10)}</td>
                <td style={{ padding: 8, fontSize: 13, color: '#444' }}>{item.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </div>
  );
}
